import Information from 'components/Information';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TableStatic from 'components/Table/TableStatic';
import {FC} from 'react';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {toIntWithCommas} from 'utils/number';

import {bodyTable} from './styles.module.css';
import {BillingDetailsBackend} from './types';

const Fy25PriceChangeForOldPlanFlaggedBanner = () => {
    const {checkFlag, data} = useBackend<BillingDetailsBackend>();

    if (!checkFlag('mep099_old_plan_banner') || !data.isOldPlan) return null;

    const announcementLink = (
        <Link
            href="https://www.makeleaps.jp/updates/2024/12/pricing-ml-2025/#03-mlsa-old-plans"
            isOpenInNewTab={true}
            key="announcementLink"
        >
            {t`the announcement`}
        </Link>
    );
    const planChangeLink = (
        <Link
            href={window.Urls.billing_plan_change_view_auth()}
            key="planChangeLink"
        >
            {t`the Change Plan page`}
        </Link>
    );

    return (
        <Information
            className="mb-2 mt-1"
            icon="info"
            size="sm"
            title={t`Discontinuation of Plans`}
            type="system"
        >
            {jt`Your current plan will be discontinued on the contract renewal date after May 31, 2025. If you wish to continue using our service, please switch to a standard plan from ${planChangeLink}. For more details, please refer to ${announcementLink} (Japanese site).`}
        </Information>
    );
};

const Details: FC = () => {
    const {
        data: {
            isBillingMethodChangeButtonShown,
            isPlanCancelButtonShown,
            partnerName,
            rows,
            showGracePeriod,
        },
    } = useBackend<BillingDetailsBackend>();

    return (
        <div className="flex flex-col gap-2">
            <NavigationTabs.Billing selection="billingDetails" />
            <Fy25PriceChangeForOldPlanFlaggedBanner />
            <TableStatic>
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column colSpan={3} id="table-username">
                            {partnerName}
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body className={bodyTable}>
                    {rows.gracePeriodPlan && (
                        <TableStatic.Row id="table-grace-period-plan">
                            <TableStatic.Cell>
                                {t`Plan`} {t`(Grace period)`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.gracePeriodPlan}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.gracePeriodAddons && (
                        <TableStatic.Row id="table-grace-period-addons">
                            <TableStatic.Cell className="align-top">
                                {t`Addons`} {t`(Grace period)`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.gracePeriodAddons.length === 0 && (
                                    <span>&mdash;</span>
                                )}
                                {rows.gracePeriodAddons.length > 0 && (
                                    <ul className="mt-0">
                                        {rows.gracePeriodAddons.map(
                                            (gracePeriodAddon) => (
                                                <li key={gracePeriodAddon}>
                                                    {gracePeriodAddon}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.newPlanStart && (
                        <TableStatic.Row id="table-new-plan-start">
                            <TableStatic.Cell>
                                {t`New plan effective on`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.newPlanStart}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.plan && (
                        <TableStatic.Row id="table-plan">
                            <TableStatic.Cell>{t`Plan`}</TableStatic.Cell>
                            <TableStatic.Cell>{rows.plan}</TableStatic.Cell>
                            <TableStatic.Cell className="flex flex-col gap-2">
                                <LinkButton
                                    aria-label="change plan"
                                    className="w-fit"
                                    href={window.Urls.billing_index()}
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t`Change Plan`}
                                </LinkButton>
                                {isPlanCancelButtonShown && (
                                    <LinkButton
                                        aria-label="cancel plan"
                                        className="w-fit"
                                        href={window.Urls.billing_cancel_questionnaire()}
                                        kind="destructive"
                                        size="xs"
                                    >
                                        {t`Cancel Plan`}
                                    </LinkButton>
                                )}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.sendingOrders && (
                        <TableStatic.Row id="table-send-orders">
                            <TableStatic.Cell>
                                {t`Send Orders`} <br />
                                <small className="opacity-60">
                                    {t`Your limit resets`}{' '}
                                    {rows.sendingOrders.resetDate}
                                </small>
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {t`${rows.sendingOrders.sentCount}/${rows.sendingOrders.limit} (sent/limit)`}
                                {rows.sendingOrders.sentCount >=
                                    rows.sendingOrders.limit && (
                                    <>
                                        <br />
                                        <div
                                            className="text-sm text-gold-600"
                                            role="alert"
                                        >
                                            {t`Adjust your limit to continue sending`}
                                        </div>
                                    </>
                                )}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="change limit"
                                    href={window.Urls.change_sendingorder_limit()}
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t`Change Limit`}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.teamMembers && rows.teamMembers.limit && (
                        <TableStatic.Row id="table-team-members">
                            <TableStatic.Cell>
                                {t`Team Members`}{' '}
                                {showGracePeriod && t`(Grace period)`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {t`${rows.teamMembers.memberCount}/${rows.teamMembers.limit} (licensed/limit)`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="change member limit"
                                    href={window.Urls.billing_index()}
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t`Change Team Member Limit`}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.clients && rows.clients.limit && (
                        <TableStatic.Row id="table-clients">
                            <TableStatic.Cell>
                                {t`Clients`}{' '}
                                {showGracePeriod && t`(Grace period)`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {t`${rows.clients.clientCount}/${rows.clients.limit} (active/limit)`}
                                {rows.clients.clientCount >=
                                    rows.clients.limit && (
                                    <>
                                        <br />
                                        <div
                                            className="text-sm text-gold-600"
                                            role="alert"
                                        >
                                            {t`Adjust your limit to add more clients`}
                                        </div>
                                    </>
                                )}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="change member limit"
                                    href={window.Urls.billing_index()}
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t`Change Client Limit`}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.addons && (
                        <TableStatic.Row id="table-addons">
                            <TableStatic.Cell className="align-top">
                                {t`Addons`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.addons.length === 0 && (
                                    <span>&mdash;</span>
                                )}
                                {rows.addons.length > 0 && (
                                    <ul className="mt-0">
                                        {rows.addons.map((addon) => (
                                            <li key={addon}>{addon}</li>
                                        ))}
                                    </ul>
                                )}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.virtualAccounts && (
                        <TableStatic.Row id="table-virtual-accounts">
                            <TableStatic.Cell className="align-top">
                                {t`Virtual Accounts`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {t`${rows.virtualAccounts.virtualAccountCount}/${rows.virtualAccounts.limit} (in use/limit)`}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.price && (
                        <TableStatic.Row id="table-price">
                            <TableStatic.Cell>{t`Price`}</TableStatic.Cell>
                            <TableStatic.Cell>{rows.price}</TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.billingFrequency && (
                        <TableStatic.Row id="table-billing-frequency">
                            <TableStatic.Cell>
                                {t`Billing Frequency`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.billingFrequency}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.billingMethod && (
                        <TableStatic.Row id="table-billing-method">
                            <TableStatic.Cell>
                                {t`Billing Method`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.billingMethod}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {isBillingMethodChangeButtonShown && (
                                    <LinkButton
                                        aria-label="billing method"
                                        href={window.Urls.billing_change_payment_details_view()}
                                        kind="secondary"
                                        size="xs"
                                    >
                                        {t`Change Billing Method`}
                                    </LinkButton>
                                )}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.billingAddress && (
                        <TableStatic.Row id="table-billing-contact">
                            <TableStatic.Cell>
                                {t`Billing Contact`}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.billingAddress}
                                <br />
                                {rows.billingEmail}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="billing contact"
                                    href={window.Urls.billing_change_payment_details_view()}
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t`Change Billing Contact`}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    <TableStatic.Row id="table-receipts">
                        <TableStatic.Cell>{t`Receipts`}</TableStatic.Cell>
                        <TableStatic.Cell />
                        <TableStatic.Cell>
                            <LinkButton
                                aria-label="Request receipts"
                                href={window.Urls.request_receipts_view()}
                                kind="secondary"
                                size="xs"
                            >
                                {t`Request Receipts`}
                            </LinkButton>
                        </TableStatic.Cell>
                    </TableStatic.Row>
                    {rows.mailPoints !== undefined && (
                        <TableStatic.Row id="table-mailpoints">
                            <TableStatic.Cell>{t`MailPoints`}</TableStatic.Cell>
                            <TableStatic.Cell>
                                {toIntWithCommas(rows.mailPoints)}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="Buy Mailpoints"
                                    href={window.Urls.billing_mailpoints_react_view()}
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t`Buy MailPoints`}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                </TableStatic.Body>
            </TableStatic>
        </div>
    );
};

export default Details;
